// Prüfung vor dem Absenden ("Fast geschafft"): fehlende Pflichtangaben als
// "Bitte noch ergänzen", sonst nur echte Lücken als freiwillige Tipps.
//
// Zwei Quellen:
//   * Regeln aus packages/shared/src/pruefung.ts (dieselben, mit denen der
//     Server das Absenden prüft) – sofort, auch offline.
//   * KI-Prüfung der Fotos (fehlende Wände, übersehene Räume, unbrauchbare
//     Fotos) – läuft bei jedem Öffnen dieser Seite; der Server prüft nur
//     Räume neu, an denen sich etwas geändert hat. Ohne Netz oder ohne KI
//     entfallen nur diese Hinweise, Absenden bleibt möglich.

import { useEffect, useState } from "react";
import { checkDraft, floorLabel, floorSketchSvg, type Problem } from "@umzugsscan/shared";
import { ApiError } from "../api.ts";
import { floorRank, navForProblem } from "../ablauf.ts";
import { useDraft, useStore, useSyncStatus } from "../hooks.ts";
import { useNav } from "../Erfassung.tsx";
import { errorText } from "../texte.ts";
import { Button, Footer } from "../ui/elemente.tsx";
import { toCheckDraft } from "../zustand.ts";

export function Pruefung() {
  const d = useDraft();
  const store = useStore();
  const status = useSyncStatus();
  const { zurueck } = useNav();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverProblems, setServerProblems] = useState<Problem[] | null>(null);
  const [ki, setKi] = useState<{ state: "laeuft" | "fertig" | "aus"; problems: Problem[] }>({ state: "laeuft", problems: [] });

  useEffect(() => {
    let alive = true;
    // Ohne Netz würde precheck() auf die Übertragung warten; nach 90 Sekunden
    // (Live: 9 Räume in ca. 20 s) ohne KI-Hinweise weiter; Senden ist dann frei.
    const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Zeitüberschreitung")), 90_000));
    Promise.race([store.precheck(), timeout]).then(
      (r) => alive && setKi({ state: r.available ? "fertig" : "aus", problems: r.problems }),
      () => alive && setKi({ state: "aus", problems: [] }),
    );
    return () => {
      alive = false;
    };
  }, [store]);

  // KI-Hinweise zu inzwischen gelöschten Fotos/Räumen ausblenden.
  const kiProblems = ki.problems.filter(
    (p) =>
      (p.target.kind !== "foto" || d.photos.some((f) => f.id === p.target.id)) &&
      (p.target.kind !== "raum" || d.rooms.some((r) => r.id === p.target.id)),
  );
  const problems = serverProblems ?? [...checkDraft(toCheckDraft(d)), ...kiProblems];
  const blocking = problems.filter((p) => p.severity === "fehlt");
  const hints = problems.filter((p) => p.severity === "hinweis");

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      await store.submit();
    } catch (e) {
      if (e instanceof ApiError && e.code === "unvollstaendig") setServerProblems(e.details.problems as Problem[]);
      else setError(errorText(e));
    } finally {
      setSending(false);
    }
  };

  const fix = (p: Problem) => {
    setServerProblems(null);
    store.go(navForProblem(p, d));
  };

  // Senden erst nach der Fotoprüfung (Live-Durchlauf 08.10.2026: Abschicken
  // nach 2 s → Hinweise ungesehen und die KI doppelt bezahlt). Ohne Netz/KI
  // oder nach Zeitüberschreitung ist "aus" – dann geht Senden sofort.
  const checking = ki.state === "laeuft";
  const tips = hints.length;

  // Grundriss aller Etagen (30.10.2026): Räume als Blasen, Antippen führt zu den Fotos des Raums.
  const etagen = [...new Set(d.rooms.map((r) => r.floor ?? 0))].sort((a, b) => floorRank(a) - floorRank(b));
  const sketches = etagen.flatMap((f) => {
    const svg = floorSketchSvg(d.rooms, f, { title: f === 0 ? "Skizze der Wohnung" : `Skizze ${floorLabel(f)}` });
    return svg ? [{ f, svg }] : [];
  });
  const zumRaum = (e: React.MouseEvent) => {
    const id = (e.target as Element).closest("[data-room]")?.getAttribute("data-room");
    if (id) fix({ code: "ansehen", severity: "hinweis", message: "", target: { kind: "raum", id } });
  };

  return (
    <>
      <h1>{blocking.length === 0 ? "Fast geschafft!" : "Es fehlt noch etwas"}</h1>
      <p className="lead">
        {blocking.length === 0
          ? d.company.privat
            ? "Danke für Ihre Fotos. Noch ein kurzer Blick, dann ermittelt die KI Volumen und Umfang Ihres Umzugs."
            : `Danke für Ihre Fotos. Noch ein kurzer Blick, dann geht alles an ${d.company.name}.`
          : "Ein paar Angaben brauchen wir noch, dann können Sie abschicken."}
      </p>

      {d.survey.reopenNote && (
        <section className="card card-warn" role="note">
          <h2>{d.company.name} bittet um weitere Fotos</h2>
          <p className="bitte">{d.survey.reopenNote}</p>
          <p className="hint">Ihre bisherigen Fotos und Angaben bleiben erhalten. Ergänzen Sie, was gewünscht ist, und schicken Sie noch einmal ab.</p>
          <button type="button" className="link" onClick={() => store.go({ screen: "fotos", guide: 0, phase: "rundum", returnToCheck: true })}>
            Zu den Fotos
          </button>
        </section>
      )}

      {checking ? (
        <section className="card ki-check" role="status">
          <span className="spinner spinner-small" aria-hidden="true" /> Wir sehen uns Ihre Fotos kurz an … Das dauert meist unter einer Minute.
        </section>
      ) : ki.state === "fertig" && kiProblems.length === 0 ? (
        <section className="card card-ok" role="status">
          <p>✓ Ihre Fotos sind gut verwertbar.</p>
        </section>
      ) : null}

      {blocking.length > 0 && (
        <section className="card card-error">
          <h2>Bitte noch ergänzen</h2>
          <ProblemList items={blocking} onFix={fix} label="Ergänzen" />
        </section>
      )}
      {tips > 0 && (
        <section className="card">
          <h2>{tips === 1 ? "Ein Tipp" : `${tips} Tipps`} für ein genaueres Angebot</h2>
          <p className="hint">Freiwillig – Sie können auch so abschicken.</p>
          <ProblemList items={hints} onFix={fix} label="Ansehen" />
        </section>
      )}

      {/* Wohnungsskizze (15.10.2026): so, wie wir die Wohnung aus Flur und Türen verstanden haben. */}
      {sketches.length > 0 && (
        <section className="card">
          <h2>Ihr Grundriss</h2>
          <p className="hint">Tippen Sie auf einen Raum, um seine Fotos noch einmal anzusehen.</p>
          {sketches.map((s) => (
            <div key={s.f}>
              {sketches.length > 1 && <h3>{s.f === 0 ? "Wohnungsebene" : floorLabel(s.f)}</h3>}
              <div className="flurskizze-rahmen grundriss-klick" aria-label="Skizze der Wohnung" onClick={zumRaum} dangerouslySetInnerHTML={{ __html: s.svg }} />
            </div>
          ))}
          <p className="hint">Stimmt etwas nicht, ändern Sie es im Grundriss oder im jeweiligen Raum.</p>
        </section>
      )}

      <section className="card summary">
        <p>
          {d.rooms.length} Räume · {d.photos.length} Fotos
          {d.photos.some((p) => p.marks.length > 0) && ` · ${d.photos.reduce((n, p) => n + p.marks.length, 0)} Markierungen`}
        </p>
        {status.pending > 0 && (
          <p className="hint">
            {status.state === "offline"
              ? `Offline: ${status.pending} Änderung(en) werden übertragen, sobald wieder Internet da ist.`
              : `Wird übertragen … (${status.pending})`}
          </p>
        )}
      </section>

      {error && <p className="error">{error}</p>}
      <Footer onBack={zurueck}>
        <Button onClick={submit} disabled={blocking.length > 0 || sending || checking}>
          {sending
            ? status.pending > 0
              ? "Fotos werden übertragen …"
              : "Wird abgeschickt …"
            : checking
              ? "Fotos werden geprüft …"
              : d.company.privat
                ? "Abschließen und weiter zur Zahlung"
                : `An ${d.company.name} senden`}
        </Button>
      </Footer>
    </>
  );
}

function ProblemList(props: { items: Problem[]; onFix: (p: Problem) => void; label: string }) {
  return (
    <ul className="problems">
      {props.items.map((p, i) => (
        <li key={`${p.code}-${p.target.id ?? i}`}>
          <span>{p.message}</span>
          <button type="button" className="link" onClick={() => props.onFix(p)}>
            {props.label}
          </button>
        </li>
      ))}
    </ul>
  );
}
